import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Check, X, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

export default function AdminReferralsTab({ getAuthHeader }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/referrals', { headers: getAuthHeader() });
      setRows(res.data.referrals || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (filter === 'converted' && !r.converted_to_thrive) return false;
    if (filter === 'pending' && r.converted_to_thrive) return false;
    if (filter === 'rewarded' && !r.free_month_issued) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [
        r.referrer_email, r.referrer_email_resolved, r.referrer_name,
        r.referred_email, r.referred_email_resolved, r.referred_name,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const stats = {
    total: rows.length,
    converted: rows.filter((r) => r.converted_to_thrive).length,
    rewarded: rows.filter((r) => r.free_month_issued).length,
  };

  return (
    <div className="space-y-5" data-testid="admin-referrals-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center">
            <Gift className="text-teal" size={20} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-navy">Referrals</h2>
            <p className="text-xs text-text-mid">Members who invited friends, and which ones converted to Thrive.</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          className="rounded-full"
          data-testid="refresh-referrals-btn"
        >
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="referrals-stats">
        <div className="bg-white rounded-2xl shadow-card p-4 border border-border-subtle">
          <p className="text-xs text-text-mid">Total Referrals</p>
          <p className="text-2xl font-bold text-navy">{stats.total}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-4 border border-border-subtle">
          <p className="text-xs text-text-mid">Converted to Thrive</p>
          <p className="text-2xl font-bold text-navy">{stats.converted}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-4 border border-border-subtle">
          <p className="text-xs text-text-mid">Free Months Issued</p>
          <p className="text-2xl font-bold text-teal-deep">{stats.rewarded}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="max-w-xs rounded-xl"
          data-testid="referrals-search-input"
        />
        <div className="flex gap-1">
          {[
            { v: '', label: 'All' },
            { v: 'pending', label: 'Pending' },
            { v: 'converted', label: 'Converted' },
            { v: 'rewarded', label: 'Rewarded' },
          ].map((f) => (
            <Button
              key={f.v}
              size="sm"
              variant={filter === f.v ? 'default' : 'ghost'}
              onClick={() => setFilter(f.v)}
              className={`rounded-full text-xs h-7 ${filter === f.v ? 'bg-teal text-white hover:bg-teal-deep' : 'text-text-mid'}`}
              data-testid={`filter-${f.v || 'all'}-btn`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-border-subtle overflow-hidden">
        {loading ? (
          <p className="p-6 text-text-mid text-sm"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-text-light text-sm" data-testid="referrals-empty">No referrals match.</p>
        ) : (
          <Table data-testid="referrals-table">
            <TableHeader>
              <TableRow>
                <TableHead>Referrer</TableHead>
                <TableHead>Referred Friend</TableHead>
                <TableHead>Signed Up</TableHead>
                <TableHead>Converted</TableHead>
                <TableHead>Free Month</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`referral-row-${r.id}`}>
                  <TableCell>
                    <div className="text-sm text-navy font-medium">{r.referrer_name || '—'}</div>
                    <div className="text-[11px] text-text-light">{r.referrer_email_resolved || r.referrer_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-navy font-medium">{r.referred_name || '—'}</div>
                    <div className="text-[11px] text-text-light">{r.referred_email_resolved || r.referred_email}</div>
                  </TableCell>
                  <TableCell className="text-xs text-text-mid">{fmtDate(r.signup_at)}</TableCell>
                  <TableCell>
                    {r.converted_to_thrive ? (
                      <Badge className="bg-teal/15 text-teal-deep">
                        <Check size={12} className="mr-1" /> {fmtDate(r.converted_at)}
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-text-mid"><X size={12} className="mr-1" /> Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.free_month_issued ? (
                      <Badge className="bg-green-100 text-green-700">Issued</Badge>
                    ) : r.converted_to_thrive ? (
                      <Badge className="bg-amber-100 text-amber-800">Pending credit</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-text-mid">—</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
